import SearchIcon from '@mui/icons-material/Search'
import { Alert, Box, Button, Snackbar, TextField, Typography, Select, MenuItem } from '@mui/material'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import React, { useCallback, useEffect, useState } from 'react'
import useSWR from 'swr'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { LoadingNormal } from '~/src/components/organisms/Loading/LoadingNormal'
import { TableListItem } from '~/src/components/pages/ListItem/components/TableListItem'
import { ProtectedPage } from '~/src/components/templates'
import { otherRepository } from '~/src/repositories'
import { stockRepository } from '~/src/repositories'
import { IGetStockResponse, PatchStockRequest } from '~/src/repositories/Stock/models/Stock'

const limit = 20

export const ListItem: React.VFC = (): JSX.Element => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()

  const [offset, setOffset] = useState<number>(0)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [listStockItem, setListStockItem] = useState<IGetStockResponse>()
  const [keyword, setKeyword] = useState<string>('')
  const [stockType, setStockType] = useState<string>('')
  const [loadingPortId, setLoadingPortId] = useState<string>('')
  const [supplierId, setSupplierId] = useState<string>('')
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [error, setError] = useState<string>('')
  const { data: loadingPorts } = useSWR(['/loading-ports'], () =>
    otherRepository.getLoadingPorts(),
    {
      fallbackData: [],
    }
  )
  const { data: suppliers } = useSWR(['/suppliers'], () =>
    otherRepository.getSuppliers(),
    {
      fallbackData: [],
    }
  )

  const handleGetStockItem = async (): Promise<void> => {
    try {
      setIsLoading(true)
      const data = await stockRepository.getStockItem({
        keyword,
        stockType,
        loadingPortId,
        supplierId,
        limit,
        offset: offset + 1, // ペジネーションのoffsetは0から始まるため+1
      })
      setListStockItem(data)
      /* eslint-disable  @typescript-eslint/no-explicit-any */
    } catch (e: any) {
      setOpenSnackbar(true)
      setError(e.message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSearchStock = async (e: React.ChangeEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    setOffset(0)
    try {
      const data = await stockRepository.getStockItem({
        keyword,
        stockType,
        loadingPortId,
        supplierId,
        limit,
        offset: offset + 1,
      })
      setListStockItem(data)
      /* eslint-disable  @typescript-eslint/no-explicit-any */
    } catch (e: any) {
      setOpenSnackbar(true)
      setError(e.message)
    }
  }

  const handleChangeKeyword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(event.target.value as string)
  }

  const handleChangeStockType = (id: string) => {
    if (stockType === id) {
      setStockType('')
      return
    }
    setStockType(id)
  }

  const listCheckStockType = [
    { id: 1, title: `${t('list_item.ckd_vehicle_only')}`, stockType: 'CkdCar' },
    { id: 2, title: `${t('list_item.engine_only')}`, stockType: 'Engine' },
    { id: 3, title: `${t('list_item.yb_vehicle_only')}`, stockType: 'YbCar' },
  ]

  const handleChangeStockItem = async (stockId: number, tags: PatchStockRequest): Promise<void> => {
    setIsLoading(true)
    try {
      await stockRepository.patchStockDetail(stockId, tags)
      await handleGetStockItem()
    } catch (e: any) {
      setOpenSnackbar(true)
      setError(e.message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleChangeLoadingPort = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLoadingPortId(event.target.value);
  };

  const handleChangeSupplier = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSupplierId(event.target.value);
  };

  useEffect(() => {
    handleGetStockItem()
  }, [offset])

  const getKeyByValue = (objects: {id: number, name: string}[], value: unknown) => {
    if (!value) return null
    return objects.find(obj => obj.id === value).name
  }

  if (isLoading) {
    return <LoadingNormal />
  }

  return (
    <ProtectedPage pageTitle={t('list_item.list_of_listed_products')}>
      <Box
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
        sx={{ mb: '24px', mt: '4px' }}
      >
        <Snackbar
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', ml: 16 }}
          autoHideDuration={4000}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={openSnackbar}
          onClose={() => setOpenSnackbar(false)}
          message={error}
          key={error + 'verticalHorizontal'}
        >
          <Alert onClose={() => setOpenSnackbar(false)} severity="error" sx={{ width: '500px' }}>
            {error}
          </Alert>
        </Snackbar>
        <form onSubmit={handleSearchStock}>
          <Box display={'flex'} alignItems={'center'}>
            {listCheckStockType.map((item) => (
              <FormControlLabel
                key={item.id}
                control={
                  <Checkbox
                    value={stockType}
                    checked={stockType === item.stockType}
                    onChange={(e) => handleChangeStockType(item.stockType)}
                    size={'small'}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontFamily: 'Roboto',
                      fontWeight: 400,
                      lineHeight: '24px',
                    }}
                  >
                    {item.title}
                  </Typography>
                }
              />
            ))}
            <Select
              name="supplierId"
              value={supplierId}
              displayEmpty
              renderValue={(value) =>
                getKeyByValue(suppliers, value) || t('list_item.supplier')
              }
              inputProps={{ 'aria-label': 'Without label' }}
              onChange={handleChangeSupplier}
              sx={{
                color: !supplierId ? 'rgba(0,0,0,0.5)' : '1',
                width: '220px',
                fontSize: '16px',
                lineHeight: '24px',
                fontStyle: 'normal',
                height: '43px',
                background: 'white',
                marginRight: '8px',
              }}
            >
              <MenuItem value=""></MenuItem>
              {suppliers.map((supplier, index) => (
                <MenuItem key={index} value={supplier.id}>
                  {supplier.name}
                </MenuItem>
              ))}
            </Select>
            <Select
              name="loadingPortId"
              value={loadingPortId}
              displayEmpty
              renderValue={(value) =>
                getKeyByValue(loadingPorts, value) || t('list_item.loading_port')
              }
              inputProps={{ 'aria-label': 'Without label' }}
              onChange={handleChangeLoadingPort}
              sx={{
                color: !loadingPortId ? 'rgba(0,0,0,0.5)' : '1',
                width: '220px',
                fontSize: '16px',
                lineHeight: '24px',
                fontStyle: 'normal',
                height: '43px',
                background: 'white',
                marginRight: '8px',
              }}
            >
              <MenuItem value=""></MenuItem>
              {loadingPorts.map((port, index) => (
                <MenuItem key={index} value={port.id}>
                  {port.name}
                </MenuItem>
              ))}
            </Select>
            <TextField
              value={keyword}
              onChange={handleChangeKeyword}
              sx={{
                minWidth: 220,
                '& .MuiOutlinedInput-input': {
                  padding: '10px 12px',
                  fontSize: 16,
                  background: 'white',
                },
              }}
              placeholder={`${t('list_item.keyword')}`}
              id="outlined-basic"
              variant="outlined"
            />
            <Button variant="contained" sx={{ marginLeft: '8px', height: '42px' }} type={'submit'}>
              <SearchIcon fontSize={'small'} />
              <Typography
                sx={{
                  marginLeft: '12px',
                  fontStyle: 'normal',
                  fontSize: 14,
                  lineHeight: '24px',
                  fontFamily: 'Roboto',
                }}
              >
                {t('list_item.search')}
              </Typography>
            </Button>
          </Box>
        </form>
      </Box>
      {!isLoading && listStockItem && (
        <TableListItem
          onSubmit={handleChangeStockItem}
          pagination={listStockItem?.pagination}
          items={listStockItem?.items}
          offset={offset}
          setOffset={setOffset}
        />
      )}
    </ProtectedPage>
  )
}
